import type { ImgHTMLAttributes } from "react";
import React from "react";

import cn from "classnames";

import { Box, boxForwardRef } from "../../../../helpers/common/Box/Box";
import { createDisplayNameForMediaElement } from "../../utils/getDisplayName";
import { createTestIdForMediaElement } from "../../utils/getTestId";
import Blur from "../Blur/MediaBlur";

import type { BoxComponentWithDefault } from "../../../../helpers/common/Box/Box";
import type { PropsWithClassName } from "../../../../types/react";
import type { ObjectFit } from "../../types";

import classes from "./MediaContent.styl";

const CONTENT_TEST_ID = createTestIdForMediaElement("content");

export type ContentProps = Pick<
    ImgHTMLAttributes<ImgHTMLAttributes<HTMLImageElement>>,
    "alt"
> & {
    imgSrc?: string;
    backgroundBlur?: boolean;
    objectFit?: ObjectFit;
    objectPosition?: React.CSSProperties["objectPosition"];
};

export const Content = boxForwardRef<
    BoxComponentWithDefault<"img", PropsWithClassName<ContentProps>>
>(
    (
        {
            as = "div",
            imgSrc,
            alt,
            backgroundBlur,
            objectFit = "fill",
            objectPosition = "center",
            className,
            ...restProps
        },
        ref,
    ) => {
        return (
            <div
                className={cn(classes.container, className)}
                data-tsid={CONTENT_TEST_ID}
            >
                {imgSrc && (
                    <>
                        {backgroundBlur && <Blur src={imgSrc} />}
                        <img
                            src={imgSrc}
                            alt={alt}
                            className={classes.content}
                            style={{ objectFit, objectPosition }}
                        />
                    </>
                )}
                <div className={classes.content}>
                    <Box as={as} {...restProps} ref={ref} />
                </div>
            </div>
        );
    },
);

Content.displayName = createDisplayNameForMediaElement("Content");

export default Content;
